// src/pages/campaigns/forms/CampaignBasicInfoForm.jsx
import React from 'react';
import {
  Grid,
  Divider,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Autocomplete,
  Typography,
  CircularProgress,
} from '@mui/material';
import { TextField } from '../../../design-system';
import {
  Campaign as CampaignIcon,
  Business as BusinessIcon,
  Style as StyleIcon,
  Event as EventIcon,
  Share as PlatformIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';

// Import shared form components
import { EntityBasicInfoForm } from '../../../components/shared/forms';
import { FormSection } from '../../../design-system';

// Campaign types options
const CAMPAIGN_TYPES = [
  { value: 'awareness', label: 'Brand Awareness' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'conversion', label: 'Conversion' },
  { value: 'product_launch', label: 'Product Launch' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'event', label: 'Event' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'brand_building', label: 'Brand Building' },
  { value: 'other', label: 'Other' }
];

// Campaign status options
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

// Platform options for checkboxes
const PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'googleAds', label: 'Google Ads' },
];

/**
 * Campaign basic information form component using shared EntityBasicInfoForm
 */
const CampaignBasicInfoForm = ({ 
  campaignData, 
  onChange, 
  onNestedChange, 
  clients = [], 
  brands = [],
  isEditMode = false,
  brandsLoading = false,
  errors = {},
  touched = {},
  data // Support for newer data prop pattern
}) => {
  // Support both campaignData and data prop patterns
  const formData = data || campaignData || {};
  
  // Log client and brand data
  console.log('📋 Client & Brand Arrays:', {
    clients: clients.slice(0, 2),
    brands: brands.slice(0, 2),
    clientsLength: clients.length,
    brandsLength: brands.length
  });
  
  // Much simpler approach using the clients array directly
  // MagicSelect will now handle the synthetic values for us
  const clientOptions = clients.length > 0 ? clients.map(client => ({
    // Just pass the label property
    label: client.name || 'Unnamed Client',
    
    // Try to include original ID properties for debugging
    ...(client._id ? { _id: client._id } : {}),
    ...(client.id ? { id: client.id } : {})
  })) : [
    // Provide fallback options if clients array is empty
    { label: 'Aghrba Internal' },
    { label: 'Saeed Alrashdi' }
  ];
  
  console.log('📊 Client Options:', clientOptions.slice(0, 3));
  
  // Brand options with similar approach
  const brandOptions = brandsLoading ? 
    [{ label: 'Loading...' }] :
    (brands.length > 0 ? 
      brands.map(brand => ({
        // Just pass the label property
        label: brand.name || 'Unnamed Brand',
        
        // Try to include original ID properties for debugging
        ...(brand._id ? { _id: brand._id } : {}),
        ...(brand.id ? { id: brand.id } : {})
      })) : 
      [{ label: 'Select Brand' }]
    );
    
  console.log('📊 Brand Options:', brandOptions.slice(0, 3));
  
  // Handle date change
  const handleDateChange = (name, value) => {
    onNestedChange('dates', name, value ? value.toISOString() : null);
  };
  
  // Handle platform checkbox change
  const handlePlatformChange = (e) => {
    const { name, checked } = e.target;
    onNestedChange('platforms', name, checked);
  };
  
  // Handle other platforms change
  const handleOtherPlatformsChange = (e, value) => {
    onNestedChange('platforms', 'other', value || []);
  };
  
  // Define the basic form fields - reorganized for better business flow
  const basicFields = [];
  
  // Define client relationship field
  const clientRelationship = {
    name: 'clientId',
    label: 'Client',
    required: true,
    disabled: isEditMode,
    options: clientOptions,
    disabledHelperText: isEditMode ? 'Client cannot be changed after campaign creation' : '',
    icon: <BusinessIcon />,
    gridSize: { xs: 12, md: 6 }
  };
  
  // Define brand relationship field with conditional loading state
  const brandRelationship = {
    name: 'brandId',
    label: 'Brand',
    required: true,
    disabled: isEditMode || !formData.clientId,
    options: brandsLoading ? [{ value: '', label: 'Loading brands...' }] : 
             brands.length === 0 ? [{ value: '', label: 'No brands available for this client' }] :
             brandOptions,
    disabledHelperText: isEditMode ? 'Brand cannot be changed after campaign creation' :
                        !formData.clientId ? 'Select a client first to see available brands' : '',
    icon: <StyleIcon />,
    gridSize: { xs: 12, md: 6 }
  };
  
  // Date picker components - these will be rendered separately
  const renderDatePickers = () => (
    <Grid container spacing={3}>
      {/* Start Date */}
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Planned Start Date *" // Add asterisk to show it's required
          value={formData.dates?.plannedStartDate ? new Date(formData.dates.plannedStartDate) : null}
          onChange={(newValue) => handleDateChange('plannedStartDate', newValue)}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: touched?.['dates.plannedStartDate'] && Boolean(errors?.['dates.plannedStartDate']),
              helperText: touched?.['dates.plannedStartDate'] && errors?.['dates.plannedStartDate']
            }
          }}
        />
      </Grid>
      
      {/* End Date */}
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Planned End Date *" // Add asterisk to show it's required
          value={formData.dates?.plannedEndDate ? new Date(formData.dates.plannedEndDate) : null}
          onChange={(newValue) => handleDateChange('plannedEndDate', newValue)}
          minDate={formData.dates?.plannedStartDate ? new Date(formData.dates.plannedStartDate) : undefined}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: touched?.['dates.plannedEndDate'] && Boolean(errors?.['dates.plannedEndDate']),
              helperText: touched?.['dates.plannedEndDate'] && errors?.['dates.plannedEndDate']
            }
          }}
        />
      </Grid>
    </Grid>
  );
  
  // Platform selection section
  const renderPlatformsSection = () => (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <PlatformIcon sx={{ mr: 1 }} /> Target Platforms
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Select the platforms where this campaign will be deployed.
      </Typography>
      
      <FormGroup>
        <Grid container spacing={2}>
          {PLATFORM_OPTIONS.map((platform) => (
            <Grid item xs={6} sm={4} md={3} key={platform.value}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.platforms?.[platform.value] || false}
                    onChange={handlePlatformChange}
                    name={platform.value}
                  />
                }
                label={platform.label}
              />
            </Grid>
          ))}
        </Grid>
      </FormGroup>
      
      {/* Other platforms */}
      <Box sx={{ mt: 2 }}>
        <Autocomplete
          multiple
          id="other-platforms"
          options={[]}
          freeSolo
          value={formData.platforms?.other || []}
          onChange={handleOtherPlatformsChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                variant="outlined"
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Other Platforms"
              placeholder="Add other platform and press Enter"
              fullWidth
            />
          )}
        />
      </Box>
    </Box>
  );
  
  // Define field groups for better organization
  const clientAndBrandFields = [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      required: true,
      disabled: isEditMode,
      options: clientOptions,
      disabledHelperText: isEditMode ? 'Client cannot be changed after campaign creation' : '',
      icon: <BusinessIcon />,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'brandId',
      label: 'Brand',
      type: 'select',
      required: true,
      disabled: isEditMode || !formData.clientId,
      options: brandsLoading ? [{ label: 'Loading brands...' }] : brandOptions,
      disabledHelperText: isEditMode ? 'Brand cannot be changed after campaign creation' :
                        !formData.clientId ? 'Select a client first to see available brands' : '',
      icon: <StyleIcon />,
      gridSize: { xs: 12, md: 6 }
    }
  ];
  
  const campaignIdentityFields = [
    {
      name: 'name',
      label: 'Campaign Name',
      type: 'text',
      required: true,
      icon: <CampaignIcon />,
      gridSize: { xs: 12, md: 12 },
      placeholder: 'e.g., Summer Sale 2025'
    },
    {
      name: 'type',
      label: 'Campaign Type',
      type: 'select',
      required: true,
      optionsKey: 'campaignType',
      icon: <StyleIcon />,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      optionsKey: 'status',
      gridSize: { xs: 12, md: 6 }
    }
  ];
  
  const campaignDetailsFields = [
    {
      name: 'brief',
      label: 'Short Brief',
      type: 'multiline',
      rows: 2,
      gridSize: { xs: 12 },
      placeholder: 'A concise summary of the campaign for quick reference...',
      helpText: 'Short summary for stakeholders (max 200 characters)',
      InputProps: { maxLength: 200 }
    },
    {
      name: 'description',
      label: 'Campaign Description',
      type: 'multiline',
      rows: 4,
      gridSize: { xs: 12 },
      placeholder: 'Provide a detailed description of the campaign objectives and strategy...'
    }
  ];
  
  return (
    <>
      {/* Section 1: Client & Brand Selection - Most Important Business Context */}
      <EntityBasicInfoForm
        data={formData}
        onChange={(nameOrValue, valueOrName) => {
          console.log('Client/Brand onChange:', { nameOrValue, valueOrName });
          onChange(nameOrValue, valueOrName);
        }}
        onNestedChange={onNestedChange}
        errors={errors}
        touched={touched}
        title="Campaign Association"
        description="Select which client and brand this campaign belongs to."
        icon={<BusinessIcon />}
        fields={clientAndBrandFields}
        options={{
          campaignType: CAMPAIGN_TYPES,
          status: STATUS_OPTIONS
        }}
        showHeader={true}
      />
      
      {/* Section 2: Campaign Identity */}
      <EntityBasicInfoForm
        data={formData}
        onChange={onChange}
        onNestedChange={onNestedChange}
        errors={errors}
        touched={touched}
        title="Campaign Identity"
        description="Define the basic details of your marketing campaign."
        icon={<CampaignIcon />}
        fields={campaignIdentityFields}
        options={{
          campaignType: CAMPAIGN_TYPES,
          status: STATUS_OPTIONS
        }}
        showHeader={true}
      />
      
      {/* Section 3: Campaign Schedule */}
      <FormSection
        title="Campaign Schedule"
        description="Set the planned start and end dates for your campaign."
        icon={<EventIcon />}
      >
        {renderDatePickers()}
      </FormSection>
      
      {/* Section 4: Campaign Details */}
      <EntityBasicInfoForm
        data={formData}
        onChange={onChange}
        onNestedChange={onNestedChange}
        errors={errors}
        touched={touched}
        title="Campaign Details"
        description="Add detailed information about your campaign."
        icon={<InfoIcon />}
        fields={campaignDetailsFields}
        options={{}}
        showHeader={true}
      />
      
      {/* Section 5: Platform Selection */}
      <FormSection
        title="Target Platforms"
        description="Select the platforms where this campaign will be deployed."
        icon={<PlatformIcon />}
      >
        {renderPlatformsSection()}
      </FormSection>
    </>
  );
};

export default CampaignBasicInfoForm;